import { Tab, TabGroup, TabList } from '@headlessui/react';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ClipboardDocumentCheckIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { fromZonedTime } from 'date-fns-tz';
import { upperFirst } from 'lodash';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useToast } from '@dashboard/context/ToastContext';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import usePage, { PageProvider } from '@dashboard/hooks/usePage';
import { confirm } from '@dashboard/lib/confirm';
import getGmtOffsetForTimezone from '@dashboard/services/getGmtOffsetForTimezone';
import logException from '@dashboard/services/logException';
import storage from '@dashboard/services/storage';
import timezones from '@dashboard/services/timezones';
import Button from '@dashboard/components/Button';
import { Form } from '@dashboard/components/Formik';
import TabButton from '@dashboard/components/TabButton';
import {
  TicketGroupEditProvider,
  useTicketGroupEdit,
} from '../PageEdit/scenes/PageEvent/components/EventTicketTypesForm/TicketGroupEditContext';
import {
  TicketGroupsProvider,
  useTicketGroups,
} from '../PageEdit/scenes/PageEvent/components/EventTicketTypesForm/TicketGroupsContext';
import ProductDetailsForm from '../PageEdit/scenes/PageProduct/components/ProductDetailsForm';
import {
  CenterCard,
  DoubleCard,
  SplitCard,
} from '../PageEdit/scenes/PageSettings/components/LayoutForm';
import TicketGroupsList from '../PageEdit/scenes/PageEvent/components/EventTicketTypesForm/components/TicketGroupsList';

const PageNew = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [tempPage, setTempPage] = useState({});
  const lastCreatedPageType = storage.local.getItem('last-created-page-type');
  const [selectedType, setSelectedType] = useState(lastCreatedPageType || 'checkout');

  function onCreatePage(page) {
    setTempPage(page);
    setNextStep();
  }

  function setNextStep() {
    setCurrentStep(currentStep + 1);
    window.scrollTo(0, 0);
  }

  return (
    <div>
      <div className="md:mt-6 pt-0.5 ml-4 absolute top-0">
        <Button appearance="light" to="/pages">
          <ChevronLeftIcon className="w-5 h-5" />
        </Button>
      </div>

      <div className={clsx('mt-20')}>
        <div className={clsx('w-full ')}>
          <div className={clsx('mx-auto px-4', {})}>
            {currentStep === 1 && (
              <SelectPageType
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                setNextStep={setNextStep}
              />
            )}

            <PageProvider pageId={tempPage._id} initialData={tempPage}>
              {currentStep === 2 && (
                <CreatePage page={tempPage} onCreatePage={onCreatePage} pageType={selectedType} />
              )}

              {(tempPage.type === PAGE_TYPES.FORM || tempPage.type === PAGE_TYPES.CHECKOUT) && (
                <>{currentStep === 3 && <PageLayout page={tempPage} setNextStep={setNextStep} />}</>
              )}

              {tempPage.type === PAGE_TYPES.EVENT && (
                <>
                  {currentStep === 3 && <CreateTicketTypes setNextStep={setNextStep} />}

                  {currentStep === 4 && <PageLayout page={tempPage} setNextStep={setNextStep} />}
                </>
              )}
            </PageProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

function getDefaultIndex(selectedType) {
  return ['checkout', 'event', 'form'].indexOf(selectedType);
}

const SelectPageType = ({ selectedType, setSelectedType, setNextStep }) => {
  return (
    <div className="max-w-screen-2xl mx-auto mb-36 px-4 sm:px-16 mt-8">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 text-center">
          <h1 className="text-2xl">Page type</h1>
          <p className="text-base font-normal mb-4">Create a checkout, event or form page</p>
        </div>

        <TabGroup
          onChange={index => setSelectedType(['checkout', 'event', 'form'][index])}
          defaultIndex={getDefaultIndex(selectedType)}
        >
          <TabList className="flex flex-col gap-4">
            <Tab className="outline-none">
              {({ selected }) => (
                <TabButton selected={selected}>
                  <div className="flex flex-row items-center gap-x-2 py-2">
                    <div className="flex items-center justify-center w-10 h-10">
                      <CreditCardIcon className="w-6 h-6" />
                    </div>
                    <div className="block text-left">
                      <div className="font-semibold">Checkout</div>
                      <div className="text-sm font-normal text-gray-700">
                        Sell products and subscriptions
                      </div>
                    </div>
                  </div>
                </TabButton>
              )}
            </Tab>
            <Tab className="outline-none">
              {({ selected }) => (
                <TabButton selected={selected}>
                  <div className="flex flex-row items-center gap-x-2 py-2">
                    <div className="flex items-center justify-center w-10 h-10">
                      <CalendarIcon className="w-6 h-6" />
                    </div>
                    <div className="block text-left">
                      <div className="font-semibold">Event</div>
                      <div className="text-sm font-normal text-gray-700">
                        Sell tickets and check-in guests
                      </div>
                    </div>
                  </div>
                </TabButton>
              )}
            </Tab>
            <Tab className="outline-none">
              {({ selected }) => (
                <TabButton selected={selected}>
                  <div className="flex flex-row items-center gap-x-2 py-2">
                    <div className="flex items-center justify-center w-10 h-10">
                      <ClipboardDocumentCheckIcon className="w-6 h-6" />
                    </div>
                    <div className="block text-left">
                      <div className="font-semibold">Form</div>
                      <div className="text-sm font-normal text-gray-700">
                        Capture leads and collect feedback
                      </div>
                    </div>
                  </div>
                </TabButton>
              )}
            </Tab>
          </TabList>
        </TabGroup>

        {selectedType && (
          <div className="mt-4">
            <Button
              appearance="primary"
              onClick={() => {
                storage.local.setItem('last-created-page-type', selectedType);

                setNextStep();
              }}
              size="full"
              arrowRight
            >
              Continue
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const CreatePage = ({ onCreatePage, pageType }) => {
  const { updatePage } = usePageQueries();

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <>
      <div className="max-w-screen-2xl mx-auto mb-36 px-4 sm:px-16 mt-8">
        <div className="mx-auto max-w-lg">
          <div className="mb-6 text-center">
            <h1 className="text-2xl">{upperFirst(pageType)} page</h1>
            <p className="text-base font-normal mb-4">
              {pageType === 'checkout' && 'Add details about your product'}
              {pageType === 'event' && 'Add details about your event'}
              {pageType === 'form' && 'Add details about your form'}
            </p>
          </div>

          {pageType === 'checkout' && (
            <ProductDetailsForm
              isNew
              onChange={async product => {
                try {
                  const { data: page, error } = await updatePage({
                    type: 'checkout',
                    name: product.title,
                    product: product._id,
                    productTitle: product.title,
                    productType: product.type,
                  });

                  if (error) throw error;

                  onCreatePage(page);
                } catch (error) {
                  console.log(error);
                  logException({
                    message: 'Failed to create checkout',
                    error,
                  });
                  window.alert('Unable to create checkout. Please try again.');
                }
              }}
            />
          )}

          {pageType === 'event' && (
            <Form
              initialValues={{
                title: '',
                eventDetails: {
                  currency: 'usd',
                  startDate: null,
                  endDate: null,
                  type: 'in_person',
                  timezone: userTimezone,
                  location: '',
                  mapsLink: '',
                  capacity: '',
                },
              }}
              onSubmit={async values => {
                try {
                  const eventDetails = {
                    ...values.eventDetails,
                  };

                  if (values.eventDetails.platform === '') {
                    eventDetails.platform = undefined;
                  }

                  if (values.eventDetails.startDate) {
                    eventDetails.startDate = fromZonedTime(
                      values.eventDetails.startDate,
                      values.eventDetails.timezone,
                    );
                  }

                  if (values.eventDetails.endDate) {
                    eventDetails.endDate = fromZonedTime(
                      values.eventDetails.endDate,
                      values.eventDetails.timezone,
                    );
                  }

                  const { data: page, error } = await updatePage({
                    name: values.title || 'Untitled event',
                    title: values.title,
                    eventDetails: eventDetails,
                    type: 'event',
                  });

                  if (error) throw error;

                  onCreatePage(page);
                } catch (error) {
                  console.log(error);
                  logException({
                    message: 'Failed to create event',
                    error,
                  });
                  window.alert('Unable to create event. Please try again.');
                }
              }}
              fields={({ values, setFieldValue }) => {
                const isCapacityReached =
                  values.eventDetails?.capacity != null &&
                  values.eventDetails?.capacity != '' &&
                  values.eventDetails?.capacity === '0';

                return [
                  {
                    name: 'title',
                    label: 'Event name',
                    type: 'text',
                    placeholder: 'Event name',
                    autoFocus: true,
                    required: true,
                  },
                  {
                    type: 'columns',
                    columns: 2,
                    fields: [
                      {
                        name: 'eventDetails.startDate',
                        label: 'Start date',
                        type: 'datetime-local',
                        required: !!values.eventDetails?.endDate,
                      },
                      {
                        name: 'eventDetails.endDate',
                        label: 'End date',
                        type: 'datetime-local',
                      },
                    ],
                  },
                  {
                    type: 'columns',
                    columns: 2,
                    fields: [
                      {
                        name: 'eventDetails.type',
                        label: 'Attendance',
                        type: 'select',
                        options: [
                          {
                            value: '',
                            label: 'Select event type',
                            disabled: true,
                          },
                          { value: 'in_person', label: 'In-person' },
                          { value: 'virtual', label: 'Virtual' },
                          { value: 'hybrid', label: 'Hybrid' },
                        ],
                      },
                      {
                        name: 'eventDetails.timezone',
                        required: false,
                        type: 'select',
                        label: 'Timezone',
                        tooltip:
                          'The timezone used for the event, on the event page and in emails to attendees',
                        options: timezones.map(tz => ({
                          value: tz,
                          label: `${tz} (GMT ${getGmtOffsetForTimezone(tz)})`
                            .replace(/_/g, ' ')
                            .replace('/', ' - ')
                            .replace('Etc - UTC', 'UTC'),
                        })),
                      },
                    ],
                    reduceMargin: values.eventDetails.type === 'in_person',
                  },
                  {
                    name: 'eventDetails.location',
                    label: 'Location',
                    type: 'text',
                    visible:
                      values.eventDetails.type === 'in_person' ||
                      values.eventDetails.type === 'hybrid',
                    reduceMargin: true,
                  },
                  {
                    name: 'eventDetails.mapsLink',
                    label: 'Maps link',
                    type: 'url',
                    visible:
                      values.eventDetails.type === 'in_person' ||
                      values.eventDetails.type === 'hybrid',
                  },
                  {
                    name: 'eventDetails.platform',
                    label: 'Platform',
                    type: 'select',
                    options: [
                      { value: '', label: 'Select platform' },
                      { value: 'zoom', label: 'Zoom' },
                      { value: 'google_meet', label: 'Google Meet' },
                      { value: 'microsoft_teams', label: 'Microsoft Teams' },
                      { value: 'youtube', label: 'YouTube' },
                      { value: 'hopin', label: 'Hopin' },
                      { value: 'vimeo', label: 'Vimeo' },
                      { value: 'discord', label: 'Discord' },
                      { value: 'other', label: 'Other' },
                    ],
                    visible:
                      values.eventDetails.type === 'virtual' ||
                      values.eventDetails.type === 'hybrid',
                  },
                  {
                    name: 'eventDetails.meetingLink',
                    label: 'Meeting link',
                    type: 'url',
                    visible:
                      values.eventDetails.type === 'virtual' ||
                      values.eventDetails.type === 'hybrid',
                    placeholder: 'https://zoom.us/j/...',
                  },
                  {
                    type: 'columns',
                    columns: 2,
                    fields: [
                      {
                        name: 'eventDetails.status',
                        label: 'Event sales',
                        type: 'select',
                        options: [
                          { value: 'active', label: 'On sale', disabled: isCapacityReached },
                          { value: 'sold_out', label: 'Sold out' },
                          { value: 'canceled', label: 'Canceled' },
                        ],
                      },
                      {
                        name: 'eventDetails.capacity',
                        label: 'Max. capacity',
                        type: 'number',
                        onChange: e => {
                          setFieldValue('eventDetails.capacity', e.target?.value);
                          if (e.target?.value === '0') {
                            setFieldValue('eventDetails.status', 'sold_out');
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: 'columns',
                    columns: 2,
                    fields: [
                      {
                        name: 'eventDetails.currency',
                        label: 'Currency',
                        type: 'currency',
                      },
                    ],
                    gap: 'sm',
                    reduceMargin: true,
                  },
                ];
              }}
              submitButton={{
                label: 'Continue',
                size: 'full',
                arrowRight: true,
              }}
              showState={false}
            />
          )}

          {pageType === 'form' && (
            <Form
              initialValues={{
                title: '',
              }}
              onSubmit={async values => {
                try {
                  const { data: page, error } = await updatePage({
                    name: values.title || 'Untitled form',
                    title: values.title,
                    type: 'form',
                  });

                  if (error) throw error;

                  onCreatePage(page);
                } catch (error) {
                  console.log(error);
                  logException({
                    message: 'Failed to create form',
                    error,
                  });
                  window.alert('Unable to create form. Please try again.');
                }
              }}
              fields={[
                {
                  name: 'title',
                  label: 'Title',
                  type: 'text',
                  placeholder: 'Title',
                  optional: true,
                  autoFocus: true,
                },
              ]}
              submitButton={{
                label: 'Continue',
                size: 'full',
                arrowRight: true,
              }}
              showState={false}
            />
          )}
        </div>
      </div>
    </>
  );
};

const PageLayout = () => {
  const { page } = usePage();
  const history = useHistory();
  const { updatePage } = usePageQueries();
  const { emitToast } = useToast();

  return (
    <>
      <div className="max-w-screen-2xl mx-auto mb-36 px-4 sm:px-16 mt-8">
        <div className="mx-auto max-w-lg">
          <div className="mb-6 text-center">
            <h1 className="text-2xl">Your {page.type} page</h1>
            <p className="text-base font-normal mb-4">How you will use your page</p>
          </div>

          <Form
            initialValues={{
              usageType: 'hosted',
              hostedLayoutType: 'center',
              siteLayoutType: 'embed',
            }}
            fields={({ values }) => [
              {
                type: 'radioCard',
                name: 'usageType',
                columns: 2,
                options: [
                  {
                    value: 'hosted',
                    label: 'Hosted page',
                    labelDescription: `Link to a hosted ${page.type} page`,
                  },
                  {
                    value: 'site',
                    label: 'Add to site',
                    labelDescription: `Add ${page.type} to your site`,
                  },
                ],
              },
              {
                type: 'radioIconCard',
                name: 'hostedLayoutType',
                label: 'Layout',
                columns: 3,
                options: [
                  {
                    value: 'center',
                    component: CenterCard,
                    label: 'Center',
                    center: true,
                  },
                  {
                    value: 'double',
                    component: DoubleCard,
                    label: 'Double',
                    center: true,
                  },
                  {
                    value: 'split',
                    component: SplitCard,
                    label: 'Split',
                    center: true,
                  },
                ],
                hidden: values.usageType !== 'hosted',
              },
              {
                type: 'radioIconCard',
                name: 'siteLayoutType',
                label: 'Layout',
                columns: 2,
                options: [
                  {
                    value: 'embed',
                    label: 'Embed',
                    component: SplitCard,
                    center: true,
                  },
                  {
                    value: 'popup',
                    label: 'Pop-up',
                    component: CenterCard,
                    center: true,
                  },
                ],
                hidden: values.usageType !== 'site',
              },
            ]}
            onSubmit={async values => {
              try {
                const { error } = await updatePage(
                  {
                    usageType: values.usageType,
                    hostedLayoutType: values.hostedLayoutType,
                    siteLayoutType: values.siteLayoutType,
                  },
                  page._id,
                );

                emitToast({
                  title: `${upperFirst(page.type)} created`,
                  description: `Your ${page.type} has been created`,
                  type: 'success',
                });

                if (error) throw error;

                history.push(`/pages/${page._id}/customize`);
              } catch (error) {
                logException({
                  message: 'Failed to create checkout',
                  error,
                });
                window.alert('Unable to create checkout. Please try again.');
              }
            }}
            submitButton={{
              label: `Create ${page.type}`,
              size: 'full',
              arrowRight: true,
            }}
            showState={false}
          />
        </div>
      </div>
    </>
  );
};

const CreateTicketTypes = ({ setNextStep }) => {
  return (
    <div className="max-w-screen-2xl mx-auto mb-36 px-4 sm:px-16 mt-8">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 text-center">
          <h1 className="text-2xl">Add tickets</h1>
          <p className="text-base font-normal mb-4">
            Set up ticket groups and types for your event
          </p>
        </div>

        <TicketGroupsProvider>
          <TicketGroupEditProvider>
            <CreateTicketTypesForm setNextStep={setNextStep} />
          </TicketGroupEditProvider>
        </TicketGroupsProvider>
      </div>
    </div>
  );
};

function CreateTicketTypesForm({ setNextStep }) {
  const { state: ticketGroups } = useTicketGroups();
  const {
    isEditingTicketType,
    isEditingTicketGroupSettings,
    isAddingTicketGroup,
    isAddingTicketType,
    hasUnsavedChanges,
  } = useTicketGroupEdit();

  return (
    <div className="flex flex-col">
      {/* Main ticket groups and types management */}
      <div>
        <TicketGroupsList isNewPageFlow={true} />
      </div>

      {!isAddingTicketType &&
        !isEditingTicketType &&
        !isAddingTicketGroup &&
        !isEditingTicketGroupSettings &&
        ticketGroups.length > 0 && (
          <div
            className={clsx('flex justify-center mt-4', {
              'px-6': ticketGroups.length === 1,
            })}
          >
            <Button
              appearance="primary"
              onClick={async () => {
                let proceed = true;

                if (hasUnsavedChanges) {
                  proceed = await confirm('Discard unsaved changes?', {
                    confirm: 'Discard',
                  });
                }

                if (!proceed) return;

                setNextStep();
              }}
              size="full"
              arrowRight
            >
              Continue
            </Button>
          </div>
        )}
    </div>
  );
}

export default PageNew;
